import Utils from '@/common/utils/utils';
import APIAmplify from '@/amplify/api';

import Constant from '@/common/constant/Constant';
import NotificationEventType from '@/common/constant/NotificationEventType';
import localStorageUtil from "@/common/utils/localStorageUtil";
import { onGetNotificationByFilter, onTicketCreated } from '@/graphql/subscriptions';
import { signOut } from '@/service/authenService';
import { notificationListAtom, notificationUnReadListAtom } from '@/state/atom/notificationAtom';
import { cloneDeep } from 'lodash';
import { getRecoil, setRecoil } from 'recoil-nexus';
import TelephoneConstant from '@/common/constant/TelephoneConstant';
import { openTelephoneTicketPanel } from '@/component/telephoneAgent/handler/TelephoneTicketPanelHandler';
import { outboundCallAtom, voiceStatusAtom } from '@/state/atom/voiceCallAtom';
import { appIntegrationSessionAtom } from '@/state/atom/appIntegrationAtom';
import { tr } from '@/common/utils/tr';
import Logger from '@/common/utils/Logger';

// list all subscription for notification
let subscriptionNotificationList = [];

// The {{key}} placeholder used in the templates written at the call sites.
const NOTIFY_NAMED_PLACEHOLDER = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
// The __n__ token actually sent to the translation engine. Tolerant of whitespace the
// engine may insert around the digits.
const NOTIFY_NUMBERED_TOKEN = /__\s*(\d+)\s*__/g;

// Japanese quotation marks 「 」 belong to the fixed frame, so tr() would hand them to the
// engine, which localizes them to " " (JP -> VI). Route them through the SAME numbered-token
// masking as the dynamic values: rewrite them to {{__quoteOpen}}/{{__quoteClose}} so they
// become __n__ tokens the engine leaves untouched, then inject the literal marks back after
// translation. Reusing the numbered mechanism means the leftover-token guard also protects
// them - if the engine mangles a token the message safely falls back to Japanese with the
// brackets still intact.
const NOTIFY_QUOTE_OPEN = '「';
const NOTIFY_QUOTE_CLOSE = '」';
const NOTIFY_QUOTE_OPEN_KEY = '__quoteOpen';
const NOTIFY_QUOTE_CLOSE_KEY = '__quoteClose';

/**
 * Turn the literal 「 」 of a template into {{__quoteOpen}}/{{__quoteClose}} placeholders and
 * report the literal value for each, so they ride the same masking path as the dynamic vars.
 *
 * @returns {{masked: string, quoteVars: Object}}
 */
// 「{{key}}」 - a placeholder wrapped directly by Japanese quotation marks.
const NOTIFY_QUOTED_PLACEHOLDER = /「\s*\{\{\s*([a-zA-Z0-9_]+)\s*\}\}\s*」/g;

/**
 * Fold 「{{key}}」 into the value itself: the template becomes {{key}} and the value becomes
 * 「value」. Masking the marks as separate tokens produced adjacent tokens '__2____3____4__'
 * which the engine treats as noise and drops (only ko kept them), so the frame always fell back
 * to Japanese. One standalone token per value is kept reliably.
 *
 * @returns {{template: string, vars: Object}}
 */
const foldQuotesIntoVars = (template, vars) => {
    const foldedVars = { ...vars };
    const folded = template.replace(NOTIFY_QUOTED_PLACEHOLDER, (match, key) => {
        const value = Object.prototype.hasOwnProperty.call(vars, key) ? (vars[key] ?? '') : '';
        foldedVars[key] = `${NOTIFY_QUOTE_OPEN}${value}${NOTIFY_QUOTE_CLOSE}`;
        return `{{${key}}}`;
    });
    return { template: folded, vars: foldedVars };
};

const maskQuotesToPlaceholders = template => {
    const quoteVars = {};
    let masked = template;
    if (masked.includes(NOTIFY_QUOTE_OPEN)) {
        masked = masked.split(NOTIFY_QUOTE_OPEN).join(`{{${NOTIFY_QUOTE_OPEN_KEY}}}`);
        quoteVars[NOTIFY_QUOTE_OPEN_KEY] = NOTIFY_QUOTE_OPEN;
    }
    if (masked.includes(NOTIFY_QUOTE_CLOSE)) {
        masked = masked.split(NOTIFY_QUOTE_CLOSE).join(`{{${NOTIFY_QUOTE_CLOSE_KEY}}}`);
        quoteVars[NOTIFY_QUOTE_CLOSE_KEY] = NOTIFY_QUOTE_CLOSE;
    }
    return { masked, quoteVars };
};

/**
 * Rewrite the named placeholders of a template into NUMBERED tokens, in order of
 * appearance: '{{user}}...{{outline}}' -> '__1__...__2__'.
 *
 * Reason: the engine treats a name like `user`/`outline`/`project` as an ordinary
 * English word, translates it and drops the braces, which destroys the placeholder
 * (observed: '{{user}}さんから...' -> 'Người dùng đã gửi...' with the values lost).
 * A `__n__` token carries no meaning to translate, and Google is known to leave that
 * shape untouched - unlike `{{n}}`, whose braces it may still strip.
 *
 * @returns {{frame: string, order: string[]}} numbered frame + key for each number
 */
const toNumberedFrame = template => {
    const order = [];
    const frame = template.replace(NOTIFY_NAMED_PLACEHOLDER, (match, key) => {
        order.push(key);
        return `__${order.length}__`;
    });
    return { frame, order };
};

/**
 * Replace __n__ tokens with their raw value (never translated) and report how many
 * were actually replaced, so the caller can detect a mangled frame.
 *
 * @returns {{result: string, injected: number}}
 */
const injectNumberedVars = (text, order, vars) => {
    let injected = 0;
    // Replacement is a function so values containing '$' are inserted literally.
    const result = text.replace(NOTIFY_NUMBERED_TOKEN, (match, token) => {
        const position = Number(token);
        if (!Number.isInteger(position) || position < 1 || position > order.length) {
            return match;
        }
        const key = order[position - 1];
        injected += 1;
        return Object.prototype.hasOwnProperty.call(vars, key) ? (vars[key] ?? '') : '';
    });
    return { result, injected };
};

/**
 * Build a notification message: translate the fixed FRAME as one unit, keep the
 * dynamic values.
 *
 * `template` is the whole Japanese sentence with every dynamic value written as a
 * {{key}} placeholder, e.g.
 *   '{{user}}さんから新規問い合わせ「{{outline}}」がありました。（{{project}}）'
 *
 * Named placeholders are first rewritten to numbered ones ({{1}}, {{2}}...), the frame
 * is translated in one piece via tr(), then the values are injected. So:
 *   - the frame is a fixed, finite set -> translated once and reused for every
 *     notification of that type (no dictionary bloat), and the sentence keeps its word
 *     order so it reads correctly in the target language;
 *   - user names, ticket outlines, project/org names and timestamps are injected AFTER
 *     translation -> never sent to the translation engine.
 *
 * Keeping the frame together with its placeholders is also what lets Translation
 * Management show the dynamic parts as read-only chips while the fixed text stays
 * editable.
 *
 * Fail-safe: fall back to the ORIGINAL Japanese message whenever the translated frame
 * comes back unusable - a placeholder was dropped (its value would silently vanish),
 * one is still left over, or anything throws.
 *
 * @param {string} template Japanese frame containing {{key}} placeholders
 * @param {Object} [vars] placeholder key -> raw value (kept untranslated)
 * @returns {string}
 */
/**
 * Translate the default name the backend writes in Japanese for an anonymous creator.
 * Any other value is user data (user, ticket, project names) and is returned as-is.
 */
const translateDefaultName = value => (value === Constant.DEFAULT_ANONYMOUS_NAME ? tr(value) : value);

const buildNotifyMsg = (template, vars = {}) => {
    // Default names are system values, so they are translated before injection; applied here so
    // every notification branch is covered.
    vars = Object.fromEntries(Object.entries(vars).map(([key, value]) => [key, translateDefaultName(value)]));
    // Mask 「 」 before numbering so they become __n__ tokens the engine leaves untouched,
    // then inject the literal marks back alongside the dynamic values.
    // 「{{key}}」 is folded into the value first; only stray marks left are masked as tokens.
    const folded = foldQuotesIntoVars(template, vars);
    const { masked, quoteVars } = maskQuotesToPlaceholders(folded.template);
    const allVars = { ...folded.vars, ...quoteVars };
    const { frame, order } = toNumberedFrame(masked);
    // Original Japanese message - the guaranteed fallback.
    const original = injectNumberedVars(frame, order, allVars).result;
    const translatedFrame = tr(frame);

    try {
        const { result, injected } = injectNumberedVars(translatedFrame, order, allVars);
        // Every placeholder must come back, otherwise its value would be lost.
        if (injected !== order.length) {
            // TODO debug only - remove before release
            return original;
        }
        // A leftover token means the frame is mangled too.
        NOTIFY_NUMBERED_TOKEN.lastIndex = 0; // stateful global regex
        if (NOTIFY_NUMBERED_TOKEN.test(result)) {
            return original;
        }

        return result;
    } catch (err) {
        Logger.error('buildNotifyMsg failed, fallback to original Japanese: ', err);
        return original;
    }
};

/**
 * Every Japanese frame a telephone/voice notification can use, flattened into one lookup
 * so `sourceInfo.notificationTemplateKey` can be resolved regardless of which backend
 * constant it came from. The frames live in Constant.js, mirroring the backend ones.
 */
const NOTIFY_TELEPHONE_TEMPLATE = {
    ...Constant.NOTIFICATION_MESSAGE_TEMPLATE,
    ...Constant.VOICE_CALL_NOTIFICATION_MESSAGE_TEMPLATE,
    ...Constant.VOICE_CALL_PIC_MESSAGE_TEMPLATE,
    ...Constant.NOTIFICATION_MESSAGE_TEMPLATE_CHAT
};

/**
 * Message for a telephone/voice notification.
 *
 * Preferred path: rebuild the sentence from the frame the backend named, so the fixed
 * text gets translated while the caller/ticket/project values are injected afterwards.
 * Rebuilding on every render also means the message follows 主言語 when it changes.
 *
 * Fallback (backend not deployed yet, or an unknown key): show `notificationContent`,
 * the sentence the backend already assembled. It is NOT passed through tr() on purpose -
 * it is a complete sentence carrying dynamic data, so translating it would miss on every
 * variant and would hand user names and ticket titles to the translation engine.
 *
 * @param {Object} sourceInfo parsed notification sourceInfo
 * @returns {string}
 */
const buildTelephoneNotifyMsg = sourceInfo => {
    const template = NOTIFY_TELEPHONE_TEMPLATE[sourceInfo?.notificationTemplateKey];

    if (!template) {
        return sourceInfo?.notificationContent ?? '';
    }

    return buildNotifyMsg(template, sourceInfo?.notificationVars ?? {});
};

// ticket created subscription instance
let ticketCreatedSubscription = null;

const getMsgWhenCreateEmailTicket = (sourceInfo) => {
    let msg = '';

    switch (sourceInfo.mailCode) {
        case Constant.NOTI_MAIL_CODE.NOTI_MAIL_RECEIVE_EMAIL_FROM_PERSIONAL_AND_CREATE_EMAIL_TICKET:
            msg = buildNotifyMsg('{{user}}さんから新規メールチケット「{{outline}}」がありました。({{project}})', {
                user: sourceInfo.sendingUserName || sourceInfo.creatorName,
                outline: sourceInfo.ticketOutline,
                project: sourceInfo.projectName
            });
            break;
        case Constant.NOTI_MAIL_CODE.NOTI_MAIL_RECEIVE_EMAIL_FROM_PROJECT_AND_CREATE_EMAIL_TICKET:
            msg = buildNotifyMsg('{{user}}から新規メールチケット「{{outline}}」がありました。({{project}})', {
                user: sourceInfo.sendingPrjName || sourceInfo.creatorName,
                outline: sourceInfo.ticketOutline,
                project: sourceInfo.projectName
            });
            break;

        default:
            msg = buildNotifyMsg('{{user}}さんから新規メールチケット「{{outline}}」がありました。({{project}})', {
                user: sourceInfo.creatorName,
                outline: sourceInfo.ticketOutline,
                project: sourceInfo.projectName
            });
            break;
    }
    
    return msg;
}

/**
 * Get notification msg
 * @param {*} notification
 * @returns notificationMsg
 */
export const getNotificationMsg = notification => {
    let sourceInfo = JSON.parse(notification.sourceInfo);
    let msg = '';
    let eventType = notification.eventType;
    let updateUserName;

    let defaultOutlineNoValue = `[${Constant.TICKET_NO_TITLE}]`;
    if (notification.objectType === Constant.NOTIFICATION_OBJECT_TYPE.EMAIL) {
        defaultOutlineNoValue = `[${Constant.EMAIL_TICKET_NO_TITLE}]`;
    }
    sourceInfo.ticketOutline = Utils.isEmptyString(sourceInfo?.ticketOutline) ? defaultOutlineNoValue : sourceInfo?.ticketOutline;
    switch (eventType) {
        // ticket
        // 1. ＜ユーザー＞さんから新規問い合わせ「＜チケット概要＞」がありました（＜プロジェクト＞））。
        case NotificationEventType.TICKET_NEW:
            let creatorName = sourceInfo.creatorName ? sourceInfo.creatorName : '';
            msg =
                creatorName !== 'anonymous'
                    ? buildNotifyMsg('{{user}}さんから新規問い合わせ「{{outline}}」がありました。（{{project}}）', {
                          user: creatorName,
                          outline: sourceInfo.ticketOutline,
                          project: sourceInfo.projectOriginName
                      })
                    : buildNotifyMsg('未登録カスタマーから新規問い合わせ「{{outline}}」がありました。', {
                          outline: sourceInfo.ticketOutline
                      });
            if (sourceInfo.anonymousType === Constant.ANONYMOUS_TYPE.CHATBOT_LIVECHAT) msg = buildNotifyMsg('ライブチャットの要求があります。');
            switch (notification.objectType) {
                // notice
                case Constant.NOTIFICATION_OBJECT_TYPE.NOTICE:
                    msg = buildNotifyMsg('{{project}}から新規お知らせ「{{outline}}」がありました。', {
                        project: sourceInfo.projectOriginName,
                        outline: sourceInfo.ticketOutline
                    });
                    break;
                // knowledge
                case Constant.NOTIFICATION_OBJECT_TYPE.KNOWLEDGE:
                    msg = buildNotifyMsg('{{project}}から新規ナレッジ「{{outline}}」がありました。', {
                        project: sourceInfo.projectOriginName,
                        outline: sourceInfo.ticketOutline
                    });
                    break;
                case Constant.NOTIFICATION_OBJECT_TYPE.REQUEST_EXTERNAL_DESK:
                    msg = buildNotifyMsg('{{project}} から新規取引申請「{{outline}}」がありました。', {
                        project: sourceInfo.projectOriginName,
                        outline: sourceInfo.ticketOutline
                    });
                    break;
                case Constant.NOTIFICATION_OBJECT_TYPE.DELEGATE:
                    msg = buildNotifyMsg('{{user}}さんから新規代理発行問い合わせ「{{outline}}」がありました。（{{project}}）', {
                        user: creatorName,
                        outline: sourceInfo.ticketOutline,
                        project: sourceInfo.projectTargetName
                    });
                    break;
                case Constant.NOTIFICATION_OBJECT_TYPE.REQUEST_EXTERNAL_CUSTOMER:
                    msg = buildNotifyMsg('{{project}} から新規取引申請「{{outline}}」がありました。', {
                        project: sourceInfo.projectOriginName,
                        outline: sourceInfo.ticketOutline
                    });
                    break;
                case Constant.NOTIFICATION_OBJECT_TYPE.QA:
                    if (sourceInfo.isNotProxiedUser) {
                        msg = buildNotifyMsg('{{user}}から新規問い合わせ「{{outline}}」がありました。', {
                            user: creatorName,
                            outline: sourceInfo.ticketOutline
                        });
                    }
                    break;
                case Constant.NOTIFICATION_OBJECT_TYPE.TASK:
                    msg = buildNotifyMsg('{{user}}さんから新規タスクチケット「{{outline}}」がありました。({{project}})', {
                        user: sourceInfo.creatorName,
                        outline: sourceInfo.ticketOutline,
                        project: sourceInfo.projectName
                    });
                    break;
                case Constant.NOTIFICATION_OBJECT_TYPE.EMAIL:
                    msg = getMsgWhenCreateEmailTicket(sourceInfo)
                    break;
                case Constant.NOTIFICATION_OBJECT_TYPE.TELEPHONE:
                    msg = buildTelephoneNotifyMsg(sourceInfo);
                    break;
                default:
                    break;
            }
            break;
        case NotificationEventType.TICKET_UPDATE:
            updateUserName = sourceInfo.creatorName ? sourceInfo.creatorName : '';
            // The action is a fixed word, so it is written into the frame instead of being a token:
            // a '__n__' verb leaves Google only particles to translate and it drops them.
            let updateActionTemplate = '{{user}}さんがチケット「{{outline}}」をしました。({{project}})';
            if (sourceInfo.approveFlag === Constant.APPROVE_FLAG.APPROVE) {
                updateActionTemplate = '{{user}}さんがチケット「{{outline}}」を承認しました。({{project}})';
            }
            if (sourceInfo.approveFlag === Constant.APPROVE_FLAG.REJECTED) {
                updateActionTemplate = '{{user}}さんがチケット「{{outline}}」を却下しました。({{project}})';
            }

            switch (notification.objectType) {
                case Constant.NOTIFICATION_OBJECT_TYPE.REQUEST_EXTERNAL_CUSTOMER:
                case Constant.NOTIFICATION_OBJECT_TYPE.REQUEST_EXTERNAL_DESK:
                case Constant.NOTIFICATION_OBJECT_TYPE.INVITATION_GUEST_DESK_PROJECT:
                case Constant.NOTIFICATION_OBJECT_TYPE.INVITATION_GUEST_PROJECT:
                    msg = buildNotifyMsg(updateActionTemplate, {
                        user: updateUserName,
                        outline: sourceInfo.ticketOutline,
                        project: sourceInfo.projectOriginName
                    });
                    break;
                case Constant.NOTIFICATION_OBJECT_TYPE.TASK:
                    msg = buildNotifyMsg('{{user}}さんがチケット「{{outline}}」を更新しました。({{project}})', {
                        user: updateUserName,
                        outline: sourceInfo.ticketOutline,
                        project: sourceInfo.projectOriginName
                    });
                    break;
                case Constant.NOTIFICATION_OBJECT_TYPE.EMAIL:
                    msg = buildNotifyMsg('{{user}}さんがメールチケット「{{outline}}」を更新しました。({{project}})', {
                        user: updateUserName,
                        outline: sourceInfo.ticketOutline,
                        project: sourceInfo.projectOriginName
                    });
                    break;
                case Constant.NOTIFICATION_OBJECT_TYPE.TELEPHONE:
                    msg = buildTelephoneNotifyMsg(sourceInfo);
                    break;
                default:
                    msg = buildNotifyMsg('{{user}}さんがチケット「{{outline}}」を更新しました。({{project}})', {
                        user: updateUserName,
                        outline: sourceInfo.ticketOutline,
                        project: sourceInfo.projectOriginName
                    });
                    break;
            }

            if (notification.objectType === Constant.NOTIFICATION_OBJECT_TYPE.QA && sourceInfo.approveFlag) {
                msg = buildNotifyMsg(updateActionTemplate, {
                    user: updateUserName,
                    outline: sourceInfo.ticketOutline,
                    project: sourceInfo.projectOriginName
                });
            }

            break;
        case NotificationEventType.UPDATE_PIC:
            updateUserName = sourceInfo.creatorName ? sourceInfo.creatorName : '';
            msg = buildNotifyMsg('あなたにチケット「{{outline}}」が割り当てられました。({{project}})', {
                outline: sourceInfo.ticketOutline,
                project: sourceInfo.projectOriginName
            });
            if (notification.objectType === Constant.NOTIFICATION_OBJECT_TYPE.EMAIL) {
                msg = buildNotifyMsg('あなたにメールチケット「{{outline}}」が割り当てられました。({{project}})', {
                    outline: sourceInfo.ticketOutline,
                    project: sourceInfo.projectOriginName
                });
                break;
            }
            if (
                notification?.objectType === Constant.NOTIFICATION_OBJECT_TYPE.TELEPHONE &&
                [
                    TelephoneConstant.METADATA_TELEPHONE_TYPE.TRAINING,
                    TelephoneConstant.METADATA_TELEPHONE_TYPE.APP_INTEGRATION
                ].includes(sourceInfo?.telephoneType)
            ) {
                msg = buildNotifyMsg('あなたにチケット「{{outline}}」の評価担当が割り当てられました。（{{project}}）', {
                    outline: sourceInfo.ticketOutline,
                    project: sourceInfo.projectOriginName
                });
                break;
            }
            if (
                notification?.objectType === Constant.NOTIFICATION_OBJECT_TYPE.TELEPHONE &&
                sourceInfo?.ticketCallType === TelephoneConstant.CALL_TYPE.VOICE_CALL
            ) {
                msg = buildTelephoneNotifyMsg(sourceInfo);
                break;
            }
            break;
        case NotificationEventType.TICKET_APPROVAL_REQUEST:
            msg = ``;
            break;
        case NotificationEventType.TICKET_DEADLINE_REACHED:
            msg = ``;
            break;
        case NotificationEventType.TICKET_APPROVAL_IN_WORKFLOW:
            msg = ``;
            break;
        case NotificationEventType.TICKET_REJECT_IN_WORKFLOW:
            msg = ``;
            break;

        // chat
        // 10. ＜ユーザー＞さんから「＜チケット概要＞」にメッセージがありました（＜プロジェクト＞））。
        case NotificationEventType.MESSAGE_NEW:
            const ticketType = sourceInfo.ticketType
            const chatSendBy = sourceInfo.chatSendBy
            const projectNameOfChat = Utils.isEmptyString(sourceInfo.affiliationProjectName)
                ? ''
                : sourceInfo.affiliationProjectName;
            if ([Constant.TICKET_TYPE.EMAIL, Constant.TICKET_TYPE.EMAIL_REPLY].includes(ticketType)) {
                switch (chatSendBy) {
                    case Constant.EMAIL_SEND_BY_OPTION.CUSTOMER_IN_DESK:
                        msg = buildNotifyMsg('{{user}}からメールチケット「{{outline}}」にメッセージがありました。（{{project}}）', {
                            user: sourceInfo.creatorName,
                            outline: sourceInfo.ticketOutline,
                            project: projectNameOfChat
                        });
                        break;
                    default:
                        msg = buildNotifyMsg('{{user}}さんからメールチケット「{{outline}}」にメッセージがありました。（{{project}}）', {
                            user: sourceInfo.creatorName,
                            outline: sourceInfo.ticketOutline,
                            project: projectNameOfChat
                        });
                }
            } else {
                msg = buildNotifyMsg('{{user}}さんから「{{outline}}」にメッセージがありました。（{{project}}）', {
                    user: sourceInfo.creatorName,
                    outline: sourceInfo.ticketOutline,
                    project: projectNameOfChat
                });
                if (notification.objectType === Constant.NOTIFICATION_OBJECT_TYPE.TELEPHONE) {
                    msg = buildTelephoneNotifyMsg(sourceInfo);
                }
            }
            break;
        case NotificationEventType.MESSAGE_MENTION_TO:
            msg = buildNotifyMsg('{{user}}さんが「{{outline}}」であなたにメンションしました。（{{project}}）', {
                user: sourceInfo.creatorName,
                outline: sourceInfo.ticketOutline,
                project: Utils.isEmptyString(sourceInfo.affiliationProjectName) ? '' : sourceInfo.affiliationProjectName
            });
            if (notification.objectType === Constant.NOTIFICATION_OBJECT_TYPE.TELEPHONE) {
                msg = buildTelephoneNotifyMsg(sourceInfo);
            }
            break;
        case NotificationEventType.MESSAGE_MENTION_ALL:
            msg = buildNotifyMsg('{{user}}さんから「{{outline}}」にメッセージがありました。（{{project}}）', {
                user: sourceInfo.creatorName,
                outline: sourceInfo.ticketOutline,
                project: Utils.isEmptyString(sourceInfo.affiliationProjectName) ? '' : sourceInfo.affiliationProjectName
            });
            if (notification.objectType === Constant.NOTIFICATION_OBJECT_TYPE.TELEPHONE) {
                msg = buildTelephoneNotifyMsg(sourceInfo);
            }

            break;
        case NotificationEventType.MESSAGE_UPDATE:
            msg = ``;
            break;

        // 13. ＜ユーザー＞さんが「＜チケット概要＞」のスレッドでスタンプを送信しました（＜プロジェクト＞）。
        case NotificationEventType.MESSAGE_STAMP_NEW:
            msg = buildNotifyMsg('＜ユーザー＞さんが「＜チケット概要＞」のスレッドでスタンプを送信しました。（＜プロジェクト＞）');
            break;
        // 14. ＜コンテンツ＞　写真、動画、音楽、PDF、ファイル。
        // <ユーザー＞さんが「＜チケット概要＞」のスレッドで＜コンテンツ＞を投稿しました（＜プロジェクト＞)。
        case NotificationEventType.MESSAGE_ATTACHMENT_NEW:
            let fileName = !Utils.isEmptyString(sourceInfo.fileName)
                ? sourceInfo.fileName.toLowerCase()
                : 'undefine';
            const attachmentVars = {
                user: sourceInfo.creatorName,
                outline: sourceInfo.ticketOutline,
                project: sourceInfo.affiliationProjectName
            };
            msg = buildNotifyMsg('{{user}}さんが「{{outline}}」のスレッドでファイルを投稿しました ({{project}})', attachmentVars);
            // images
            let isImageFile = /\.(jpe?g|tiff?|png|webp|bmp)$/i.test(fileName);
            if (isImageFile) {
                msg = buildNotifyMsg('{{user}}さんが「{{outline}}」のスレッドで写真を投稿しました。({{project}})', attachmentVars);
                break;
            }

            // gif
            let isGifFile = /\.(gif|GIF)$/i.test(fileName);
            if (isGifFile) {
                msg = buildNotifyMsg('{{user}}さんが「{{outline}}」のスレッドで動画を投稿しました。({{project}})', attachmentVars);
                break;
            }

            // music
            let isMusicFile = /\.(mp3|MP3|mpeg|MPEG|m3u|M3U)$/i.test(fileName);
            if (isMusicFile) {
                msg = buildNotifyMsg('{{user}}さんが「{{outline}}」のスレッドで音楽を投稿しました。({{project}})', attachmentVars);
                break;
            }

            // pdf
            let isPdfFile = /\.(pdf|PDF)$/i.test(fileName);
            if (isPdfFile) {
                msg = buildNotifyMsg('{{user}}さんが「{{outline}}」のスレッドでPDFを投稿しました。({{project}})', attachmentVars);
                break;
            }

            let isVideo= /\.(mp4|avi|mov|wmv|flv)$/i.test(fileName?.toLowerCase())
            if (isVideo) {
                msg = buildNotifyMsg('{{user}}さんが「{{outline}}」のスレッドで動画を投稿しました。({{project}})', attachmentVars);
                break;
            }

            msg = buildNotifyMsg('{{user}}さんが「{{outline}}」のスレッドでファイルを投稿しました。({{project}})', attachmentVars);
            break;
        // 12. あなたの「＜チケット概要＞」のメッセージに＜ユーザー＞さんがリアクションしました（＜プロジェクト＞）。
        case NotificationEventType.MESSAGE_REACTION:
            msg = buildNotifyMsg('あなたの「＜チケット概要＞」のメッセージに＜ユーザー＞さんがリアクションしました。（＜プロジェクト＞）');
            break;

        // user
        // 15. あなたにAdministrator権限が追加されました。
        case NotificationEventType.ADMIN_ON:
            msg = buildNotifyMsg('あなたに組織マネージャ―権限が追加されました。');
            break;
        // 16. あなたのAdministrator権限が削除されました。
        case NotificationEventType.ADMIN_OFF:
            msg = buildNotifyMsg('あなたの組織マネージャ―権限が削除されました。');
            break;
        // todo [Pending] 17. あなたはSolutionDesk契約のOwnerになりました。
        case NotificationEventType.OWNER_ON:
            msg = buildNotifyMsg('あなたはSolutionDesk契約のOwnerになりました。');
            break;
        // todo [Pending] 18. あなたはSolutionDesk契約のOwnerでなくなりました。
        case NotificationEventType.OWNER_OFF:
            msg = buildNotifyMsg('あなたはSolutionDesk契約のOwnerでなくなりました。');
            break;
        // 19. あなたのIDは＜以前のIDステータス＞から＜今のIDステータス＞に変更になりました。
        // General: ON/OF
        case NotificationEventType.USER_TYPE_CHANGE:
            // oldType/newType are fixed system status words -> translated on their own.
            msg = buildNotifyMsg('あなたのIDは{{oldType}}から{{newType}}に変更になりました。', {
                oldType: tr(sourceInfo.oldType),
                newType: tr(sourceInfo.newType)
            });
            break;

        // case NotificationEventType.GENERAL_OFF:
        //     msg = ``;
        //     break;

        // 22. あなたは＜プロジェクト＞のメンバーとして追加されました。
        case NotificationEventType.MEMBER_ON:
            msg = buildNotifyMsg('あなたは{{project}}のメンバーとして追加されました。', {
                project: sourceInfo.projectName
            });
            break;
        // 23. あなたは＜プロジェクト＞のメンバーから削除されました。
        case NotificationEventType.MEMBER_OFF:
            msg = buildNotifyMsg('あなたは{{project}}のメンバーから削除されました。', {
                project: sourceInfo.projectName
            });
            break;
        // 24. あなたは＜プロジェクト＞のプロジェクトマネージャーになりました。
        case NotificationEventType.PM_ON:
            msg = buildNotifyMsg('あなたは{{project}}のプロジェクトマネージャーになりました。', {
                project: sourceInfo.projectName
            });
            break;
        // 25. あなたは＜プロジェクト＞のプロジェクトマネージャーでなくなりました。
        case NotificationEventType.PM_OFF:
            msg = buildNotifyMsg('あなたは{{project}}のプロジェクトマネージャーでなくなりました。', {
                project: sourceInfo.projectName
            });
            break;
        case NotificationEventType.EXCEED_MAX_TICKET:
            msg = buildNotifyMsg('現契約で設定される発行可能なMaxのチケットの上限を超えています。容量追加オプション購入など上限拡大をお願いします。（システム）');
            break;
        case NotificationEventType.EXCEED_MAX_STORAGE:
            msg = buildNotifyMsg('現契約で設定されるストレッジ容量の上限を超えています。容量追加オプション購入など上限拡大をお願いします。（システム）');
            break;
        case NotificationEventType.EXCEED_MAX_GENERAL_USER_WHEN_CREATE_TICKET:
            msg = buildNotifyMsg('現契約で設定されるジェネラルユーザー数の上限を超えています。ジェネラルユーザー追加オプション購入など上限拡大をお願いします。（システム）');
            break;
        case NotificationEventType.KNOWLEDGE_CHAT_MESSAGE_NEW:
            if(sourceInfo.contentType === 'ATTACHMENT_FILE'){
                let nameOfFile = sourceInfo?.fileName?.toLowerCase()
                const knowledgeAttachmentVars = {
                    user: sourceInfo.creatorName,
                    title: sourceInfo.ticketTitle,
                    project: sourceInfo.affiliationProjectName
                };
                msg = buildNotifyMsg('{{user}}さんが「{{title}}」のスレッドでファイルを投稿しました ({{project}})', knowledgeAttachmentVars);
            // images
            let isImage = /\.(jpe?g|tiff?|png|webp|bmp)$/i.test(nameOfFile);
            if (isImage) {
                msg = buildNotifyMsg('{{user}}さんが「{{title}}」のスレッドで写真を投稿しました。({{project}})', knowledgeAttachmentVars);
                break;
            }

            // gif
            let isGif = /\.(gif|GIF)$/i.test(nameOfFile);
            if (isGif) {
                msg = buildNotifyMsg('{{user}}さんが「{{title}}」のスレッドで動画を投稿しました。({{project}})', knowledgeAttachmentVars);
                break;
            }

            // music
            let isMusic = /\.(mp3|MP3|mpeg|MPEG|m3u|M3U)$/i.test(nameOfFile);
            if (isMusic) {
                msg = buildNotifyMsg('{{user}}さんが「{{title}}」のスレッドで音楽を投稿しました。({{project}})', knowledgeAttachmentVars);
                break;
            }

            // pdf
            let isPdf = /\.(pdf|PDF)$/i.test(nameOfFile);
            if (isPdf) {
                msg = buildNotifyMsg('{{user}}さんが「{{title}}」のスレッドでPDFを投稿しました。({{project}})', knowledgeAttachmentVars);
                break;
            }

            let isVideoChatKnowledge= /\.(mp4|avi|mov|wmv|flv)$/i.test(nameOfFile)
            if (isVideoChatKnowledge) {
                msg = buildNotifyMsg('{{user}}さんが「{{title}}」のスレッドで動画を投稿しました。({{project}})', knowledgeAttachmentVars);
                break;
            }

            msg = buildNotifyMsg('{{user}}さんが「{{title}}」のスレッドでファイルを投稿しました。({{project}})', knowledgeAttachmentVars);

            }else{
                msg = buildNotifyMsg('{{user}}さんから「{{title}}」にメッセージがありました。（{{project}}）', {
                    user: sourceInfo.creatorName,
                    title: sourceInfo.ticketTitle,
                    project: Utils.isEmptyString(sourceInfo.affiliationProjectName) ? '' : sourceInfo.affiliationProjectName
                });
                break;
            }
            break;
        case NotificationEventType.MENTION_IN_CHAT_KNOWLEDGE:
            msg = buildNotifyMsg('{{user}}さんが「{{title}}」であなたにメンションしました。（{{project}}）', {
                user: sourceInfo.creatorName,
                title: sourceInfo.ticketTitle,
                project: Utils.isEmptyString(sourceInfo.affiliationProjectName) ? '' : sourceInfo.affiliationProjectName
            });
            break;
        case NotificationEventType.NOTI_UPDATE_CONTRACT_CREDIT_CARD:
            let organizationNameCreditCard =  Utils.isEmptyString(sourceInfo.organizationName)
            ? ''
            : sourceInfo.organizationName
            msg = buildNotifyMsg('{{org}}は支払方法をクレジットカードに変更しました。', {
                org: organizationNameCreditCard
            });
            break;
        case NotificationEventType.NOTI_UPDATE_CONTRACT_BANK_TRANSFER:
            let organizationName =  Utils.isEmptyString(sourceInfo.organizationName)
            ? ''    
            : sourceInfo.organizationName
            msg = buildNotifyMsg('{{org}}は支払方法を振込に変更しました。', {
                org: organizationName
            });
            break;
        case NotificationEventType.OVER_MAX_RESOURCES:
            let itemOverMax = Utils.isEmptyString(sourceInfo.itemOverMax)? ''
            : sourceInfo.itemOverMax
            // itemOverMax is a fixed system resource name -> translated on its own.
            msg = buildNotifyMsg('{{item}}の上限を超えています。追加オプション購入など上限拡大をお願いします。', {
                item: tr(itemOverMax)
            });
            break;
        case NotificationEventType.NOTI_ORG_REGISTERED_TRIAL:
            let organizationRegisteredName =  Utils.isEmptyString(sourceInfo.organizationName)
            ? ''
            : sourceInfo.organizationName
            let organizationRegisteredDomain =  Utils.isEmptyString(sourceInfo.organizationDomain)
            ? ''
            : sourceInfo.organizationDomain
            let organizationRegisteredTime =  Utils.isEmptyString(sourceInfo.createdAt)
            ? ''
            : sourceInfo.createdAt
            msg = buildNotifyMsg('{{org}}({{domain}})はお試し契約を登録しました。\n登録日時：{{time}}', {
                org: organizationRegisteredName,
                domain: organizationRegisteredDomain,
                time: organizationRegisteredTime
            });
            break; 
        case NotificationEventType.NOTI_ORG_STARTED_TRIAL:
            let organizationStartedTrialName =  Utils.isEmptyString(sourceInfo.organizationName)
            ? ''
            : sourceInfo.organizationName
            let organizationStartedTrialDomain =  Utils.isEmptyString(sourceInfo.organizationDomain)
            ? ''
            : sourceInfo.organizationDomain
            let organizationStartedTrialTime =  Utils.isEmptyString(sourceInfo.createdAt)
            ? ''
            : sourceInfo.createdAt
            msg = buildNotifyMsg('{{org}}({{domain}})はお試し契約を開始しました。\n登録日時：{{time}}', {
                org: organizationStartedTrialName,
                domain: organizationStartedTrialDomain,
                time: organizationStartedTrialTime
            });
            break;  
        case NotificationEventType.NOTI_YOURS_CONTRACT_HAVE_BEEN_UPDATED:
            msg = buildNotifyMsg('契約変更が完了されました。ご確認ください。');
            break;  
        default:
            break;
    }
    return msg;
};

/**
 * Check notification user
 * @param {*} notification
 * @param {*} argsCommon
 * @returns isNotification
 */
const isNotificationUser = (notification, argsCommon) => {
    let eventType = notification.eventType;

    // check type msg
    let isUserChange = [
        NotificationEventType.ADMIN_ON,
        NotificationEventType.ADMIN_OFF,
        NotificationEventType.OWNER_ON,
        NotificationEventType.OWNER_OFF,
        NotificationEventType.GENERAL_ON,
        NotificationEventType.GENERAL_OFF,
        NotificationEventType.MEMBER_ON,
        NotificationEventType.MEMBER_OFF,
        NotificationEventType.PM_ON,
        NotificationEventType.PM_OFF,
        NotificationEventType.USER_TYPE_CHANGE
    ].includes(eventType);
    if (!isUserChange) {
        return false;
    }

    let userDetail = argsCommon.userDetail;

    // user notification
    return userDetail.id === notification.notificationRange;
};

const isNotificationUserMustLogout = (notification, argsCommon) => {
    let eventType = notification.eventType;
    let sourceInfo = JSON.parse(notification.sourceInfo);
    let userDetail = argsCommon.userDetail;
    if (userDetail.id !== notification.notificationRange) {
        return false;
    }
    // user origin setting change 
    let isUserSettingChange = [
        NotificationEventType.ADMIN_ON,
        NotificationEventType.ADMIN_OFF,
        NotificationEventType.OWNER_ON,
        NotificationEventType.OWNER_OFF,
        NotificationEventType.GENERAL_ON,
        NotificationEventType.GENERAL_OFF,
        NotificationEventType.USER_TYPE_CHANGE
    ].includes(eventType);
    if (isUserSettingChange) {
        return true;
    }

    // not in project => not force logout
    const projectId = localStorageUtil.getSelectedProjectId();
    
   
    // check type msg
    let isUserProjectChange = [
        NotificationEventType.PM_ON,
        NotificationEventType.PM_OFF,
        // NotificationEventType.MEMBER_ON,
        NotificationEventType.MEMBER_OFF,
    ].includes(eventType);
    if (sourceInfo?.projectId !== projectId) {
        return false;
    }
   
    if (isUserProjectChange) {
        return true;
    }

    return false;
};

/**
 * Check notification ticket
 * @param {*} notification
 * @param {*} argsCommon
 * @returns isNotification
 */

const isNotificationTicket = (notification, argsCommon) => {
    
    let eventType = notification.eventType;
    
    let isNoticeTicket = notification.objectType === 'Notice';
    let sourceInfo = Utils.convertJSONDataToObject(notification.sourceInfo);
    const prefixOfPublicRange = Utils.getPrefixOfId(sourceInfo.publicRange);
    // list project Id
    let projectListId = [];
    let projectList = argsCommon.projectList;
    
    if (!Utils.isEmptyArray(projectList)) {
        projectList?.forEach(project => {
            projectListId.push(project.id);
        });
    }

    // check type msg
    let isTicket = [
        NotificationEventType.TICKET_NEW,
        NotificationEventType.TICKET_UPDATE,
        NotificationEventType.TICKET_APPROVAL_REQUEST,
        NotificationEventType.TICKET_DEADLINE_REACHED,
        NotificationEventType.TICKET_APPROVAL_IN_WORKFLOW,
        NotificationEventType.TICKET_REJECT_IN_WORKFLOW,
        NotificationEventType.UPDATE_PIC
    ].includes(eventType);
    if (!isTicket) {
        return false;
    }
    
    const userDetail = argsCommon.userDetail;

    if (userDetail.id === sourceInfo.creatorId) {
        return false;
    }
    if (userDetail.id === sourceInfo.updatorId) {
        return false;
    }
    
    // Handle TICKET_NEW event for TELEPHONE ticket only
    // TELEPHONE tickets send notification directly to B2C user 
    if (eventType === NotificationEventType.TICKET_NEW && 
        notification.objectType === Constant.NOTIFICATION_OBJECT_TYPE.TELEPHONE) {
        // Check if notification is sent directly to this user
        if (notification.notificationRange === userDetail.id) {
            return true;
        }
        
        // Check if notification is sent to user's current project or any project user belongs to
        if (notification.notificationRange === argsCommon.projectDetail.id || 
            projectListId.includes(notification.notificationRange)) {
            return true;
        }
    }
    
    if (eventType === NotificationEventType.UPDATE_PIC || eventType === NotificationEventType.TICKET_UPDATE) {
        if (
            notification.notificationRange === userDetail.id 
        ){
            return true;
        }
        else if(notification.notificationRange === argsCommon.projectDetail.id || projectListId.includes(notification.notificationRange)){
            return true
            /* let projectListUserInfo =  projectList.filter(item => item.id === notification.notificationRange);
            let filterCreatedAtResult = false;
            projectListUserInfo?.forEach(projectUserInfo => {
                if (projectUserInfo.userAddAt && notification.createdAt) {
                    let filterResult = filterCreateAtTime(
                        notification.createdAt,
                        projectUserInfo.userAddAt
                    );
                    filterCreatedAtResult = filterResult ? true : filterCreatedAtResult;
                }
            });
            return filterCreatedAtResult */
        }

        // switch (notification.notificationRange) {
        //     case Constant.NOTICE_GROUP_CUSTOMER.ALL_GENERAL_PROJECT:
        //     case Constant.NOTICE_GROUP_CUSTOMER.ALL_CUSTOMER_PROJECT:
        //     case Constant.NOTICE_GROUP_CUSTOMER.ALL_GUEST_CUSTOMER_USER:
        //         if (
        //             notification.sourceInfo && JSON.parse(notification.sourceInfo) &&
        //             JSON.parse(notification.sourceInfo).projectTarget.includes(argsCommon.projectDetail.id)
        //         ) return true
        //         return false
            
        //     case Constant.NOTICE_GROUP_CUSTOMER.ALL_USER_IN_BASE_PROJECT:
        //         let sourceInfo = notification.sourceInfo ? JSON.parse(notification.sourceInfo) : null
        //         if (
        //             notification.affiliationOrganization === argsCommon.projectDetail.affiliationOrganization
        //         ) return true
        //         return false

        //     default:
        //         break;
            
        // }

        return false
    }
    

    if (isNoticeTicket) {
        if (notification.notificationRange === argsCommon.userDetail.id) {
            return true;
        } else if (
            notification.notificationRange === argsCommon.projectDetail.id ||
            projectListId.includes(notification.notificationRange)
        ) {
            /* let projectListUserInfo = projectList.filter(
                item => item.id === notification.notificationRange
            );
            let filterCreatedAtResult = false;
            projectListUserInfo?.forEach(projectUserInfo => {
                if (projectUserInfo.userAddAt && notification.createdAt) {
                    let filterResult = filterCreateAtTime(
                        notification.createdAt,
                        projectUserInfo.userAddAt
                    );
                    filterCreatedAtResult = filterResult ? true : filterCreatedAtResult;
                }
            });
            return filterCreatedAtResult; */
            return true
        }

        return false;
        
    }
    
    if (
        notification.notificationRange === argsCommon.userDetail.id &&
        argsCommon.userDetail.id === sourceInfo.proxiedUser &&
        notification.objectType === 'DELEGATE'
    ) {
        return true;
    }
    // ticket notification
    // check memeber of project
    
    let projectTarget = Utils.isEmptyArray(sourceInfo.projectTarget)
        ? []
        : Utils.convertJSONDataToObject(sourceInfo.projectTarget);

        
    if (notification.objectType === 'KNOWLEDGE') {

        
        if (
            prefixOfPublicRange === 'corp' ||
            sourceInfo.publicRange === Constant.KNOWLEDGE_PUBLIC_RANGE.ALL
        ) {
            projectTarget = projectTarget.concat([sourceInfo.projectOrigin]);
        }
        projectTarget = Utils.isEmptyString(sourceInfo.publicRange)
            ? projectTarget
            : projectTarget.concat([sourceInfo.publicRange]);
    }
    
    // Ticket external customer
    if (notification.objectType === Constant.NOTIFICATION_OBJECT_TYPE.REQUEST_EXTERNAL_CUSTOMER) {
        if (notification.notificationRange === argsCommon.userDetail.id) {
            return true;
        } else if (
            notification.notificationRange === argsCommon.projectDetail.id ||
            projectListId.includes(notification.notificationRange)
        ) {
            /* let projectListUserInfo = projectList.filter(
                item => item.id === notification.notificationRange
            );
            let filterCreatedAtResult = false;
            projectListUserInfo?.forEach(projectUserInfo => {
                if (projectUserInfo.userAddAt && notification.createdAt) {
                    let filterResult = filterCreateAtTime(
                        notification.createdAt,
                        projectUserInfo.userAddAt
                    );
                    filterCreatedAtResult = filterResult ? true : filterCreatedAtResult;
                }
            });
            return filterCreatedAtResult; */
            return true
        }

        return false;
    }

    let isMemberProject = false;
    
    
    
    for (let index in projectList) {
        isMemberProject = projectTarget.includes(projectList[index].id);
        if(isMemberProject){
            break;
        }
        
        // if (isMemberProject) {
        //     let filterResult = false;
        //     if (projectList[index]?.userAddAt) {
        //         filterResult = filterCreateAtTime(
        //             notification.createdAt,
        //             projectList[index]?.userAddAt
        //         );
        //     }
        //     if (filterResult) break;
        //     isMemberProject = false
        // }
    }
    
    return isMemberProject;
};

/**
 * Check notification chat
 * @param {*} notification
 * @param {*} argsCommon
 * @returns isNotification
 */
const isNotificationChat = (notification, argsCommon) => {
    let eventType = notification.eventType;

    // check type msg
    let isMsgChat = [
        NotificationEventType.MESSAGE_NEW,
        NotificationEventType.MESSAGE_UPDATE,
        NotificationEventType.MESSAGE_STAMP_NEW,
        NotificationEventType.MESSAGE_ATTACHMENT_NEW,
        NotificationEventType.MESSAGE_REACTION,
        NotificationEventType.MESSAGE_MENTION_TO,
        NotificationEventType.KNOWLEDGE_CHAT_MESSAGE_NEW,
        NotificationEventType.MENTION_IN_CHAT_KNOWLEDGE
    ].includes(eventType);
    if (!isMsgChat) {
        return false;
    }

    if(eventType === NotificationEventType.KNOWLEDGE_CHAT_MESSAGE_NEW){
        return true
    }

    if(eventType === NotificationEventType.MENTION_IN_CHAT_KNOWLEDGE){
        return true
    }

    let userDetail = argsCommon.userDetail;
    let projectList = argsCommon.projectList;
    let exceptUserList = [];

    //Notification to mention user with event MESSAGE_MENTION_TO

    let sourceInfo = JSON.parse(notification.sourceInfo);
    if (sourceInfo.exceptUserList) {
        exceptUserList = Utils.convertJSONDataToObject(sourceInfo.exceptUserList);
    }
    if (exceptUserList.includes(userDetail.id)) {
        return false;
    }
    const prefixOfNotificationRange = Utils.getPrefixOfId(notification.notificationRange);
    let isMemberProject = false;
    if (prefixOfNotificationRange === 'user') {
        isMemberProject = userDetail.id === notification.notificationRange;
    }
    if (prefixOfNotificationRange === 'project') {
        isMemberProject = projectList.filter(item => item.id === notification.notificationRange).length > 0
        /* let projectListUserInfo =  projectList.filter(item => item.id === notification.notificationRange);
        projectListUserInfo?.forEach((projectUserInfo) =>{
            if(projectUserInfo.createdAt && notification.createdAt){
                let filterResult = filterCreateAtTime(notification.createdAt,projectUserInfo.createdAt)
                isMemberProject = filterResult? true : isMemberProject
            }
        }) */
    }
    // check member of project

    return isMemberProject && userDetail.id !== sourceInfo.creatorId;
};

const isNotificationFromExceedMaxTicket = (notification, argsCommon) => {
    let eventType = notification.eventType;
    if(eventType === Constant.NOTIFICATION_EVENT_TYPE.EXCEED_MAX_TICKET) {
        let userDetail = argsCommon.userDetail;
        return userDetail.id === notification.notificationRange;
    }

    return false;
}
const isNotificationFromExceedMaxGeneralWhenCreateTicket = (notification, argsCommon) => {
    let eventType = notification.eventType;
    if(eventType === Constant.NOTIFICATION_EVENT_TYPE.EXCEED_MAX_GENERAL_USER_WHEN_CREATE_TICKET) {
        let userDetail = argsCommon.userDetail;
        return userDetail.id === notification.notificationRange;
    }

    return false;
}

const isNotificationUpdatedContractOrgRoot = (notification, argsCommon) => {
    let eventType = notification.eventType;
    if(eventType === Constant.NOTIFICATION_EVENT_TYPE.NOTI_YOURS_CONTRACT_HAVE_BEEN_UPDATED) {
        let userDetail = argsCommon.userDetail;
        return userDetail.id === notification.notificationRange;
    }

    return false;
}

const isNotificationOverMaxResources = (notification, argsCommon) => {
    let eventType = notification.eventType;
    if(eventType === Constant.NOTIFICATION_EVENT_TYPE.OVER_MAX_RESOURCES) {
        let userDetail = argsCommon.userDetail;
        return userDetail.id === notification.notificationRange;
    }

    return false;
}

const isNotificationOrgRegister = (notification, argsCommon) => {
    let eventType = notification.eventType;
    if (
        [
            Constant.NOTIFICATION_EVENT_TYPE.NOTI_ORG_REGISTERED_TRIAL,
            Constant.NOTIFICATION_EVENT_TYPE.NOTI_ORG_STARTED_TRIAL
        ].includes(eventType)
    ) {
        if (!argsCommon?.userDetail || !argsCommon?.corporationDetail) return false;
        let userSettingValue = Utils.convertJSONDataToObject(argsCommon?.userDetail?.userSetting);
        return (
            (userSettingValue?.adminMode === Constant.ADMIN_MODE.ON &&
                Utils.isAdminUser(argsCommon?.userDetail) &&
                argsCommon.corporationDetail?.rootFlag)
        );
    }

    return false;
};

const isNotificationContract = (notification, argsCommon) => {
    let eventType = notification.eventType;
    if (
        [
            Constant.NOTIFICATION_EVENT_TYPE.NOTI_UPDATE_CONTRACT_BANK_TRANSFER,
            Constant.NOTIFICATION_EVENT_TYPE.NOTI_UPDATE_CONTRACT_CREDIT_CARD
        ].includes(eventType)
    ) {
        let userDetail = argsCommon.userDetail;
        return userDetail.id === notification.notificationRange;
    }

    return false;
};

const isReceiveNotification = (notification, argsCommon) => {
    let userDetail = argsCommon.userDetail;
    let settingNotification = userDetail.settingNotification;
    return true;
    // if settingNotification empty then return true
    /* if (Utils.isEmptyObject(settingNotification)) {
        return true;
    }

    try {
        settingNotification = JSON.parse(settingNotification);
    } catch (err) {
        return true;
    }

    let eventType = notification.eventType;
    // chat
    let isMsgChat = [
        NotificationEventType.MESSAGE_NEW,
        NotificationEventType.MESSAGE_UPDATE,
        NotificationEventType.MESSAGE_STAMP_NEW,
        NotificationEventType.MESSAGE_ATTACHMENT_NEW,
        NotificationEventType.MESSAGE_REACTION,
        NotificationEventType.MESSAGE_MENTION_TO
    ].includes(eventType);

    if (isMsgChat) {
        let isSDeskNotificationChatMsg = settingNotification.chatMsg.isSdeskNotification;
        return Utils.isEmpty(isSDeskNotificationChatMsg) ? true : isSDeskNotificationChatMsg;
    }

    // user change
    let isUserChange = [
        NotificationEventType.ADMIN_ON,
        NotificationEventType.ADMIN_OFF,
        NotificationEventType.OWNER_ON,
        NotificationEventType.OWNER_OFF,
        NotificationEventType.GENERAL_ON,
        NotificationEventType.GENERAL_OFF,
        NotificationEventType.MEMBER_ON,
        NotificationEventType.MEMBER_OFF,
        NotificationEventType.PM_ON,
        NotificationEventType.PM_OFF,
        NotificationEventType.USER_TYPE_CHANGE
    ].includes(eventType);
    if (isUserChange) {
        let isSDeskNotificationUserChange = settingNotification.userChange.isSdeskNotification;
        return Utils.isEmpty(isSDeskNotificationUserChange) ? true : isSDeskNotificationUserChange;
    }

    // ticket
    let isTicket = [
        NotificationEventType.TICKET_NEW,
        NotificationEventType.TICKET_UPDATE,
        NotificationEventType.TICKET_APPROVAL_REQUEST,
        NotificationEventType.TICKET_DEADLINE_REACHED,
        NotificationEventType.TICKET_APPROVAL_IN_WORKFLOW,
        NotificationEventType.TICKET_REJECT_IN_WORKFLOW
    ].includes(eventType);
    if (isTicket) {
        let isSDeskNotificationTicket = settingNotification.ticket.isSdeskNotification;
        return Utils.isEmpty(isSDeskNotificationTicket) ? true : isSDeskNotificationTicket;
    } */
};

/**
 * Update notification list in state
 * @param {*} notification
 * @param {*} argsCommon
 */
const updateNotificationListState = (notification, argsCommon) => {
    
    // check setting receive notification
    let isReceive = isReceiveNotification(notification, argsCommon);
    
    if (!isReceive) {
        return;
    }

    // user notification
    let isNotificationFromUser = isNotificationUser(notification, argsCommon);

    // ticket notification
    let isNotificationFromTicket = isNotificationTicket(notification, argsCommon);
    

    // chat notification
    let isNotificationFromChat = isNotificationChat(notification, argsCommon);

    // exceed setting max ticket notification
    let isNotificationExceedMaxTicket = isNotificationFromExceedMaxTicket(notification, argsCommon);

    // exceed setting max general when create ticket notification
    let isNotificationExceedMaxGeneralWhenCreateTicket = isNotificationFromExceedMaxGeneralWhenCreateTicket(notification, argsCommon);

    let isNotificationFromContract = isNotificationContract(notification, argsCommon);

    let isNotificationFromOverMaxResources = isNotificationOverMaxResources(notification, argsCommon);
    let isNotificationFromOrgRegister = isNotificationOrgRegister(notification, argsCommon);
    let isNotificationUpdatedContractOrg = isNotificationUpdatedContractOrgRoot(notification, argsCommon);

    let isNotification =
        isNotificationFromUser ||
        isNotificationFromTicket ||
        isNotificationFromChat ||
        isNotificationExceedMaxTicket ||
        isNotificationExceedMaxGeneralWhenCreateTicket ||
        isNotificationFromContract ||
        isNotificationFromOverMaxResources ||
        isNotificationFromOrgRegister ||
        isNotificationUpdatedContractOrg;

    if (isNotification) {
        // update post msg
        // let notificationUnReadListState = cloneDeep(argsCommon.notificationUnReadListState);
        let notificationUnReadListState = cloneDeep(getRecoil(notificationUnReadListAtom));

        // build msg
        notification.notificationMsg = getNotificationMsg(notification);
        notificationUnReadListState = [notification].concat(notificationUnReadListState);
        argsCommon.setNotificationUnReadListState(notificationUnReadListState);

        // update all msg
        // let notificationAllListState = cloneDeep(argsCommon.notificationAllListState);
        let notificationAllListState = cloneDeep(getRecoil(notificationListAtom));
        notificationAllListState = [notification].concat(notificationAllListState);


        argsCommon.setNotificationAllListState(notificationAllListState);

        if (isNotificationFromUser && isNotificationUserMustLogout(notification, argsCommon)) {
            signOut();
        }
    }
};

/**
 * Unsubscribe all with: event delete, update chat post
 */
export const unsubscribeEventNotification = () => {
    let isUnsubscribeEventNotification = false;
    if (Utils.isEmptyArray(subscriptionNotificationList)) {
        return isUnsubscribeEventNotification;
    }

    // unsubscription all notification
    subscriptionNotificationList.forEach(sub => {
        if (sub.subscription?._state !== 'closed') {
            sub.subscription?.unsubscribe();
            isUnsubscribeEventNotification = true;
        } 
    });
    subscriptionNotificationList = [];
    return isUnsubscribeEventNotification;
};

/**
 * Listen subscription event create notification
 * @param {*} API
 * @param {*} allState
 */
export const onSubscriptionEventCreateNotification = (API, allState) => {
    // check empty and exists of list
    // if subscriptionNotificationList exists then break;
    
    if (!Utils.isEmptyArray(subscriptionNotificationList)) {
        return;
    }
    let listNotificationRangeId = [allState?.userDetail?.id,allState?.corporationDetail?.id];
    allState?.projectList?.forEach(project => {
        listNotificationRangeId.push(project.id)
    });
    // unsub
    
    const listSubscription = multiConnectSubscription(listNotificationRangeId,allState);
    

    // push subscription to list
    subscriptionNotificationList.push(...listSubscription)
};

const renderFilterNotification = (listNotificationRange) => {
    if(listNotificationRange.length === 0) return {
        "notificationRange" : {
            "in": []
        }
    }
    let notificationRangeFilter = [];
    let subListNotificationRange = []
    for(let i = 0; i < listNotificationRange.length; i++ ){
        if(subListNotificationRange.length === 5){
            notificationRangeFilter.push(
                {
                    "notificationRange" : {
                        "in": subListNotificationRange
                    } 
                }
            )
            subListNotificationRange = [];
            subListNotificationRange.push(listNotificationRange[i]);
            continue;
        }else if(i === listNotificationRange.length - 1){
            subListNotificationRange.push(listNotificationRange[i]);
            notificationRangeFilter.push(
                {
                    "notificationRange" : {
                        "in": subListNotificationRange
                    } 
                }
            )
            subListNotificationRange = [];
            continue;
        }
        subListNotificationRange.push(listNotificationRange[i]);

    }
    return notificationRangeFilter;

}

const connectNotificationSubscription = (listNotificationRangeId,allState,index) => {
    let filterOr = {
        or: renderFilterNotification(listNotificationRangeId)
    };
    
    let filterString = JSON.stringify(filterOr);
    let subscription = APIAmplify.graphql({query: onGetNotificationByFilter,
        variables: { filter: filterString }
    }).subscribe({
        next: resData => {
            let msgRes = resData.value.data.onGetNotificationByFilter;

            if(msgRes?.eventType === Constant.NOTIFICATION_EVENT_TYPE.DELETE_PROJECT ){
                allState.setProjectDeletedSubscription({id: msgRes?.affiliationProject, active: Constant.ACTIVE_FLAG.DELETED});
            }else{
                // update notification list
                updateNotificationListState(msgRes, allState);
            }
            
        },
        
        error: err => {
            console.log('connectNotificationSubscription', err)
            // unsubscription event create
            let isUnsubscribeEventNotification = unsubscribeEventNotification();

            // show alert disconnect
            // if isShowDialog equals true then break
            if (!allState.isShowReconnectDialog && isUnsubscribeEventNotification === true) {
                allState.setIsShowReconnectDialog(true);
            }
        }
    });

    return subscription;
}

const multiConnectSubscription = (listNotificationRangeId,allState) => {
    let listSubscription = [];
    let listNotificationRange = []
    for(let index=0 ; index < listNotificationRangeId?.length; index++){
        if(listNotificationRange.length === 10){
            const timeStamd = new Date().getTime()
            const subscription = connectNotificationSubscription(listNotificationRange,allState,timeStamd);
            listNotificationRange = [];
            listSubscription.push({subscription: subscription});
        }

        listNotificationRange.push(listNotificationRangeId[index]);

        if(index+1 === listNotificationRangeId?.length){
            const timeStamd = new Date().getTime()
            const subscription = connectNotificationSubscription(listNotificationRange,allState,timeStamd);
            listNotificationRange = [];
            listSubscription.push({subscription: subscription});
        }

    }

    return listSubscription
}

/**
 * Subscribe to onTicketCreated event for telephone tickets
 * @param {Object} allState - contains corporationDetail, projectInuseDetail, userDetail, projectList, addNewPanel
 */
export const subscribeTicketCreated = (allState) => {
    if (ticketCreatedSubscription) {
        return;
    }

    const corporationDetail = allState?.corporationDetail;
    const projectInuseDetail = allState?.projectInuseDetail;
    const corporationId = corporationDetail?.id;
    const projectInuseId = projectInuseDetail?.id;
    if (!corporationId || !projectInuseId) {
        return;
    }

    let graphqlVariables = {
        ticketType: Constant.TICKET_TYPE.TELEPHONE,
        affiliationOrganization: corporationId,
        affiliationProject: projectInuseId,
    }

    if ([Constant.PROJECT_TYPE.BASE, Constant.PROJECT_TYPE.CUSTOMER].includes(projectInuseDetail.type)) {
        delete graphqlVariables.affiliationProject;
    }

    ticketCreatedSubscription = APIAmplify.graphql({
        query: onTicketCreated,
        variables: {...graphqlVariables}
    }).subscribe({
        next: resData => {
            const ticketData = resData?.value?.data?.onTicketCreated;
            if (!ticketData) return;

            handleTicketTelephoneCreatedEvent(ticketData, allState);
        },
        error: err => {
            console.log('[subscribeTicketCreated] error:', err);
            unsubscribeTicketCreated();
        }
    });
};

/**
 * Unsubscribe from onTicketCreated event
 */
export const unsubscribeTicketCreated = () => {
    if (ticketCreatedSubscription) {
        ticketCreatedSubscription.unsubscribe();
        ticketCreatedSubscription = null;
    }
};

/**
 * Handle ticket created event from subscription
 * @param {Object} ticketData - ticket data from subscription
 * @param {Object} allState - component state
 */
const handleTicketTelephoneCreatedEvent = async (ticketData, allState) => {
    // Only handle TELEPHONE ticket main created
    const dataType = ticketData?.dataType;
    if (!dataType || dataType !== Constant.TICKET_DATA_TYPE.TICKET) return;
    
    const userId = allState?.userDetail?.id;
    const metaData = Utils.convertJSONDataToObject(ticketData?.metaData);
    if (!metaData) return;

    // Don't subscription telephone child
    // open ticket child is handle on incoming call
    if (!Utils.isEmptyArray(ticketData?.children)) return;

    const appIntegrationSession = getRecoil(appIntegrationSessionAtom);
    const isCurrentAppIntegrationTicket =
        metaData.telephoneType === TelephoneConstant.METADATA_TELEPHONE_TYPE.APP_INTEGRATION &&
        appIntegrationSession?.sessionId === metaData.sessionId;

    if (isCurrentAppIntegrationTicket) {
        const customerChatThreadId = ticketData.chatThreadWithCustomer || null;

        setRecoil(appIntegrationSessionAtom, {
            ...appIntegrationSession,
            ticketId: ticketData.id,
            chatThreadId: customerChatThreadId,
            chatThreadData: {
                ...(appIntegrationSession.chatThreadData || {}),
                customerChatThreadId,
                projectChatThreadId: ticketData.chatThreadInProject || null,
            },
            status: 'active',
        });
        setRecoil(voiceStatusAtom, Constant.PHONE_AVAILABILITY.ACTIVE);

        if (allState?.addNewPanel) {
            await openTelephoneTicketPanel({
                ticketData: { ticketId: ticketData.id },
                projectInuseDetail: allState.projectInuseDetail,
                projectList: allState.projectList,
                addNewPanel: allState.addNewPanel,
                reloadTicketListByPanelType: allState.reloadTicketListByPanelType,
            });
        }
        return;
    }

    // Only match outbound calls (inbound handled via custom params in IncomingCallPopup)
    const outboundCall = getRecoil(outboundCallAtom);

    if (!outboundCall) return;

    const ticketCallSid = metaData?.twilioCallSid;
    const outboundCallSid = outboundCall?.callSid;

    // OR logic: match by callSid (precise) or by creator (fallback when callSid empty)
    const matchByCallSid = outboundCallSid && ticketCallSid && ticketCallSid === outboundCallSid;
    const matchByCreator = ticketData.creator === userId;

    if (!matchByCallSid && !matchByCreator) {
        return;
    }

    // Match found — open ticket panel
    const projectInuseDetail = allState?.projectInuseDetail;
    const projectList = allState?.projectList;
    const addNewPanel = allState?.addNewPanel;

    if (addNewPanel) {
        await openTelephoneTicketPanel({
            ticketData: { ticketId: ticketData.id },
            projectInuseDetail,
            projectList,
            addNewPanel,
            reloadTicketListByPanelType: allState?.reloadTicketListByPanelType
        });
    }
};
